import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { CheckCircle2, Package, Truck, X, RefreshCw, RotateCcw, Repeat, Tag, Wallet, ChevronRight, Undo2, AlertCircle } from "lucide-react";

export function OrdersPage() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then(({data}) => setOrders(data)); }, []);

  const statusColor = (s) => ({
    confirmed:"#D4FF00", processing:"#D4FF00", shipped:"#00F0FF",
    out_for_delivery:"#00F0FF", delivered:"#D4FF00",
    cancelled:"#FF007F", refunded:"#FF007F", exchanged:"#A1A1AA",
  }[s] || "#FAFAFA");

  const returnBadge = (rs) => {
    if (!rs || rs === "none") return null;
    const map = {
      requested: { label: "Return requested", color: "#00F0FF" },
      approved: { label: "Return approved", color: "#00F0FF" },
      rejected: { label: "Return rejected", color: "#FF007F" },
      picked_up: { label: "Return picked up", color: "#00F0FF" },
      received: { label: "Return received", color: "#00F0FF" },
      refunded: { label: "Return refunded", color: "#FF007F" },
    }[rs];
    if (!map) return null;
    return (
      <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded"
        style={{color: map.color, background: `${map.color}1F`}}>
        {map.label}
      </span>
    );
  };

  return (
    <div className="pb-24 md:pb-12 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <h1 className="font-display font-black text-4xl sm:text-6xl uppercase mb-6">Orders<span className="text-[#D4FF00]">.</span></h1>
      {orders.length === 0 && <div className="text-[#A1A1AA]">No orders yet.</div>}
      <div className="space-y-3">
        {orders.map(o => (
          <Link key={o.order_id} to={`/orders/${o.order_id}`} data-testid={`order-row-${o.order_id}`} className="card p-4 flex items-center gap-3">
            <Package size={20} className="text-[#D4FF00]"/>
            <div className="flex-1 min-w-0">
              <div className="font-bold">#{o.order_id.slice(-6).toUpperCase()}</div>
              <div className="text-xs text-[#A1A1AA]">{o.items.length} items · {new Date(o.created_at).toLocaleDateString()}</div>
              {returnBadge(o.return_status) && <div className="mt-1">{returnBadge(o.return_status)}</div>}
            </div>
            <div className="font-display font-bold text-[#D4FF00]">{fmtMoney(o.total)}</div>
            <div className="text-xs uppercase font-bold tracking-widest" style={{color: statusColor(o.status)}}>{o.status}</div>
            <ChevronRight size={14} className="text-[#A1A1AA]"/>
          </Link>
        ))}
      </div>
    </div>
  );
}

const STATUS_STEPS = [
  { k: "confirmed",        label: "Confirmed",      icon: CheckCircle2 },
  { k: "processing",       label: "Processing",     icon: Package },
  { k: "shipped",          label: "Shipped",        icon: Truck },
  { k: "out_for_delivery", label: "Out for delivery", icon: Truck },
  { k: "delivered",        label: "Delivered",      icon: Package },
];

export function OrderDetailPage() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [exchangeIdx, setExchangeIdx] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setO(data);
  };
  useEffect(() => { load(); }, [id]);

  const fire = (m) => { setToast(m); setTimeout(() => setToast(null), 2200); };

  const cancelOrder = async () => {
    if (!confirm("Cancel this order? Refund will go to your wallet.")) return;
    setBusy(true);
    try {
      await api.post(`/orders/${id}/cancel`, { reason: "Customer cancelled" });
      fire("Order cancelled — refunded to wallet");
      await load();
    } catch (e) {
      fire(e.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const refundOrder = async () => {
    if (!confirm("Request a refund? Amount will be credited to your wallet.")) return;
    setBusy(true);
    try {
      await api.post(`/orders/${id}/refund`, { reason: "Refund request", refund_to: "wallet" });
      fire("Refund processed to wallet");
      await load();
    } catch (e) {
      fire(e.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  if (!o) return <div className="p-10 text-center text-[#A1A1AA]">Loading…</div>;

  const isClosed = ["cancelled", "refunded"].includes(o.status);
  const isInflight = ["confirmed", "processing", "shipped", "out_for_delivery"].includes(o.status);
  const stepIdx = STATUS_STEPS.findIndex(s => s.k === o.status);

  return (
    <div className="pb-24 md:pb-12 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      {toast && <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-[#D4FF00] text-black font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-full z-50 shadow-2xl" data-testid="order-toast">{toast}</div>}

      {/* Header */}
      <div className="card p-6 mb-4">
        <CheckCircle2 size={32} className="text-[#D4FF00] mb-2"/>
        <div className="font-display font-black text-3xl uppercase">{
          o.status === "cancelled" ? "Order cancelled" :
          o.status === "refunded" ? "Order refunded" :
          o.status === "exchanged" ? "Order exchanged" :
          o.status === "delivered" ? "Delivered" : "Order placed"
        }</div>
        <div className="text-sm text-[#A1A1AA]">#{o.order_id.slice(-8).toUpperCase()} · {new Date(o.created_at).toLocaleString()}</div>
      </div>

      {/* Status timeline */}
      {!isClosed && o.status !== "exchanged" && (
        <div className="card p-4 mb-4">
          <div className="grid grid-cols-5 gap-1 mb-2">
            {STATUS_STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i <= Math.max(0, stepIdx);
              return (
                <div key={s.k} className="text-center">
                  <div className={`inline-flex w-9 h-9 rounded-full items-center justify-center mb-1 ${done?"bg-[#D4FF00] text-black":"bg-[#3D2845] text-[#A1A1AA]"}`}>
                    <Icon size={16}/>
                  </div>
                  <div className={`text-[10px] font-bold uppercase ${done?"text-white":"text-[#A1A1AA]"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Return status banner */}
      {o.return_status && o.return_status !== "none" && (
        <ReturnStatusBanner returnStatus={o.return_status}
                            refundAmount={o.return_refund_amount}
                            reason={o.return_reason}
                            items={o.items}
                            indices={o.return_items || []}/>
      )}

      {/* Tracking history */}
      {(o.tracking || []).length > 0 && (
        <div className="card p-4 mb-4">
          <div className="font-display font-bold uppercase text-sm mb-3">Tracking history</div>
          <div className="space-y-2">
            {[...(o.tracking || [])].reverse().map((t) => (
              <div key={`${t.at}-${t.status}`} className="flex gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-[#D4FF00] mt-2 shrink-0"/>
                <div className="flex-1">
                  <div className="font-bold">{t.label}</div>
                  {t.note && <div className="text-xs text-[#A1A1AA]">{t.note}</div>}
                </div>
                <div className="text-xs text-[#A1A1AA] font-mono-display">{new Date(t.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items */}
      <div className="card p-4 mb-4">
        <div className="font-display font-bold uppercase text-sm mb-3">Items</div>
        {o.items.map((it, i) => (
          <div key={`${it.product_id}-${it.size || "nosize"}-${i}`} className="flex gap-3 py-2 border-b border-[#3D2845] last:border-0 items-center">
            <img src={it.image} className="w-14 h-14 object-cover rounded-lg"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold line-clamp-1">{it.product_name}</div>
              <div className="text-xs text-[#A1A1AA]">Size: {it.size || "—"} · Qty: {it.quantity}</div>
            </div>
            <div className="font-display font-bold text-[#D4FF00]">{fmtMoney(it.price*it.quantity)}</div>
            {!isClosed && (
              <button onClick={()=>setExchangeIdx(i)} data-testid={`exchange-item-${i}`} className="chip text-xs"><Repeat size={12}/> Exchange</button>
            )}
          </div>
        ))}
      </div>

      {/* Money breakdown */}
      <div className="card p-4 mb-4">
        <div className="font-display font-bold uppercase text-sm mb-3">Payment</div>
        <div className="space-y-1 text-sm">
          {o.subtotal != null && <Row label="Subtotal" value={fmtMoney(o.subtotal)}/>}
          {(o.discount || 0) > 0 && <Row label={`Coupon ${o.coupon_code||""}`} value={`-$${o.discount.toFixed(2)}`} accent="#D4FF00"/>}
          {(o.wallet_used || 0) > 0 && <Row label="Wallet used" value={`-$${o.wallet_used.toFixed(2)}`} accent="#00F0FF"/>}
          <Row label="Method" value={(o.payment_method || "card").replace("_", " ")}/>
          {o.payment_status && (
            <Row label="Status"
              value={o.payment_status.toUpperCase()}
              accent={o.payment_status === "paid" ? "#D4FF00" : "#FFA500"}/>
          )}
          {(o.rewards_earned || 0) > 0 && <Row label="Rewards earned" value={`+$${o.rewards_earned.toFixed(2)}`} accent="#FF007F"/>}
        </div>
        <div className="border-t border-[#3D2845] pt-2 mt-2 flex justify-between font-display font-black text-xl">
          <span>Paid</span>
          <span className="text-[#D4FF00]">{fmtMoney(o.total)}</span>
        </div>
      </div>

      {/* Seller contact card(s) — surfaced after every order placement so
          the buyer has a direct way to reach the merchant. */}
      {(o.seller_payment_snapshots || []).length > 0 && (
        <div className="card p-4 mb-4" data-testid="order-seller-contact">
          <div className="font-display font-bold uppercase text-sm mb-3">Seller details</div>
          <div className="grid gap-3">
            {o.seller_payment_snapshots.map((s, i) => (
              <div key={`${s.store_id || s.method}-${i}`} className="rounded-xl bg-[#261534] border border-[#3D2845] p-3">
                <div className="font-bold text-sm mb-1.5">
                  {s.contact?.store_name || s.contact?.owner_name || "Seller"}
                </div>
                <div className="grid sm:grid-cols-3 gap-2 text-xs">
                  {s.contact?.email && <div className="break-all"><span className="text-[#A1A1AA]">Email · </span>{s.contact.email}</div>}
                  {s.contact?.phone && <div><span className="text-[#A1A1AA]">Phone · </span>{s.contact.phone}</div>}
                  {s.contact?.pickup_address && <div><span className="text-[#A1A1AA]">Address · </span>{s.contact.pickup_address}</div>}
                </div>
                {/* QR/Bank specifics so buyer can pay if order is pending */}
                {s.method === "qr" && s.config?.qr_image_url && o.payment_status === "pending" && (
                  <div className="mt-3 flex items-start gap-3">
                    <img src={s.config.qr_image_url} alt="QR" className="w-24 h-24 rounded-lg bg-white p-2 object-contain"/>
                    <div className="text-xs text-[#A1A1AA]">
                      Scan and pay {s.config.label ? `· ${s.config.label}` : ""}<br/>
                      {s.config.handle && <span className="font-mono-display text-[#D4FF00]">{s.config.handle}</span>}
                      {s.config.note && <div className="mt-1">{s.config.note}</div>}
                    </div>
                  </div>
                )}
                {s.method === "bank" && o.payment_status === "pending" && (
                  <div className="mt-3 text-xs grid gap-1">
                    {s.config?.account_name && <div><span className="text-[#A1A1AA]">Account name: </span><span className="font-bold">{s.config.account_name}</span></div>}
                    {s.config?.account_number && <div><span className="text-[#A1A1AA]">Account no: </span><span className="font-mono-display">{s.config.account_number}</span></div>}
                    {s.config?.ifsc_swift && <div><span className="text-[#A1A1AA]">IFSC / SWIFT: </span><span className="font-mono-display">{s.config.ifsc_swift}</span></div>}
                    {s.config?.note && <div className="text-[#A1A1AA]">{s.config.note}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {!isClosed && (
        <div className="flex flex-wrap gap-2 mb-4">
          {isInflight && (
            <button onClick={cancelOrder} data-testid="order-cancel-btn" disabled={busy}
              className="btn-secondary"><X size={14} className="inline mr-1"/>Cancel order</button>
          )}
          {o.status === "delivered" && (!o.return_status || o.return_status === "none" || o.return_status === "rejected") && (
            <button onClick={() => setReturnOpen(true)} data-testid="order-return-btn" disabled={busy}
              className="btn-secondary"><Undo2 size={14} className="inline mr-1"/>Request return</button>
          )}
          <button onClick={refundOrder} data-testid="order-refund-btn" disabled={busy}
            className="btn-secondary"><RefreshCw size={14} className="inline mr-1"/>Request refund</button>
        </div>
      )}

      <div className="card p-4">
        <div className="font-display font-bold uppercase text-sm mb-2">Shipping to</div>
        <div className="text-sm text-[#A1A1AA]">
          {o.address.name}<br/>{o.address.line1}<br/>{o.address.city}, {o.address.zip}<br/>{o.address.country}
        </div>
      </div>

      {exchangeIdx !== null && (
        <ExchangeModal orderId={id} item={o.items[exchangeIdx]} itemIndex={exchangeIdx}
          onClose={()=>setExchangeIdx(null)}
          onDone={(msg) => { fire(msg); setExchangeIdx(null); load(); }}/>
      )}

      {returnOpen && (
        <ReturnModal orderId={id} items={o.items}
          onClose={()=>setReturnOpen(false)}
          onDone={(msg) => { fire(msg); setReturnOpen(false); load(); }}/>
      )}
    </div>
  );
}

function ReturnStatusBanner({ returnStatus, refundAmount, reason, items, indices }) {
  const STAGES = [
    { k: "requested", label: "Requested" },
    { k: "approved",  label: "Approved" },
    { k: "picked_up", label: "Picked up" },
    { k: "refunded",  label: "Refunded" },
  ];
  const isRejected = returnStatus === "rejected";
  const idx = STAGES.findIndex(s => s.k === returnStatus);
  const itemNames = (indices || []).map(i => items?.[i]?.product_name).filter(Boolean);
  return (
    <div className={`card p-4 mb-4 border-2 ${isRejected ? "border-[#FF007F]/40" : "border-[#00F0FF]/30"}`} data-testid="return-banner">
      <div className="flex items-center gap-2 mb-3">
        <Undo2 size={16} className={isRejected ? "text-[#FF007F]" : "text-[#00F0FF]"}/>
        <div className="font-display font-bold uppercase text-sm">Return · {returnStatus.replace("_", " ")}</div>
        {refundAmount > 0 && returnStatus === "refunded" && (
          <span className="ml-auto chip active text-xs">+{fmtMoney(Number(refundAmount))} to wallet</span>
        )}
      </div>
      {itemNames.length > 0 && (
        <div className="text-xs text-[#A1A1AA] mb-2">For: {itemNames.join(", ")}</div>
      )}
      {reason && <div className="text-xs text-[#A1A1AA] mb-3">Reason: {reason}</div>}
      {!isRejected ? (
        <div className="grid grid-cols-4 gap-1">
          {STAGES.map((s, i) => {
            const done = i <= Math.max(0, idx);
            return (
              <div key={s.k} className="text-center">
                <div className={`inline-flex w-7 h-7 rounded-full items-center justify-center mb-1 ${done ? "bg-[#00F0FF] text-black" : "bg-[#3D2845] text-[#A1A1AA]"}`}>
                  <CheckCircle2 size={12}/>
                </div>
                <div className={`text-[10px] font-bold uppercase ${done ? "text-white" : "text-[#A1A1AA]"}`}>{s.label}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-[#FF007F]">
          <AlertCircle size={14}/> Return was not approved.
        </div>
      )}
    </div>
  );
}

function ReturnModal({ orderId, items, onClose, onDone }) {
  // `picked` now stores stable per-item KEYS (not indices). Indices are
  // recomputed at submit time from the items array. This makes
  // reconciliation correct even if the parent list ever reorders.
  const keyOf = (it, i) => `${it.product_id}-${it.size || "nosize"}-${i}`;
  const [picked, setPicked] = useState(() => new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const toggle = (k) => {
    const next = new Set(picked);
    if (next.has(k)) next.delete(k); else next.add(k);
    setPicked(next);
  };
  const submit = async () => {
    if (picked.size === 0) { setErr("Select at least one item to return."); return; }
    // Resolve indices from the current items array — same contract the
    // backend expects (`item_indices`).
    const idxs = items.reduce((acc, it, i) => (picked.has(keyOf(it, i)) ? [...acc, i] : acc), []);
    setBusy(true); setErr("");
    try {
      await api.post(`/orders/${orderId}/return`, { item_indices: idxs, reason });
      onDone("Return requested · seller will review");
    } catch (e) { setErr(e.response?.data?.detail || "Return failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#261534] w-full max-w-lg rounded-3xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-black uppercase text-xl flex items-center gap-2"><Undo2 size={18}/> Request return</div>
          <button onClick={onClose} data-testid="return-close-btn"><X size={18}/></button>
        </div>
        <div className="text-xs text-[#A1A1AA] mb-3">Pick the items you want to return. Refund (after seller approval + pickup) goes to your wallet.</div>
        <div className="space-y-2 mb-4">
          {items.map((it, i) => {
            const k = keyOf(it, i);
            const isOn = picked.has(k);
            return (
              <button key={k} onClick={() => toggle(k)} data-testid={`return-pick-${i}`}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${isOn ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-[#3D2845] hover:border-[#3D2845]"}`}>
                <input type="checkbox" checked={isOn} readOnly className="accent-[#00F0FF]"/>
                <img src={it.image} className="w-12 h-12 object-cover rounded-lg" alt=""/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold line-clamp-1">{it.product_name}</div>
                  <div className="text-xs text-[#A1A1AA]">Size: {it.size || "—"} · Qty: {it.quantity}</div>
                </div>
                <div className="font-display font-bold text-[#D4FF00] text-sm">{fmtMoney(it.price * it.quantity)}</div>
              </button>
            );
          })}
        </div>
        <textarea placeholder="Reason (e.g. wrong size, defective)" rows={3} value={reason}
          onChange={e => setReason(e.target.value)} className="input-dark" data-testid="return-reason"/>
        {err && <div className="text-sm text-[#FF007F] mt-2">{err}</div>}
        <button onClick={submit} disabled={busy || picked.size === 0} data-testid="return-submit-btn"
          className="btn-primary w-full mt-4">
          {busy ? "Sending…" : `Request return for ${picked.size} item${picked.size === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[#A1A1AA] capitalize">{label}</span>
      <span style={accent?{color:accent}:{}} className="font-bold">{value}</span>
    </div>
  );
}

function ExchangeModal({ orderId, item, itemIndex, onClose, onDone }) {
  const [tab, setTab] = useState("size");   // size | product
  const [newSize, setNewSize] = useState("");
  const [products, setProducts] = useState([]);
  const [pickedProduct, setPickedProduct] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (tab === "product" && products.length === 0) {
      api.get(`/orders/${orderId}/exchange-options/${itemIndex}`).then(({data}) => setProducts(data.products || []));
    }
  }, [tab, orderId, itemIndex, products.length]);

  // Get available sizes from same product (we have item.product_id but not product details)
  const [allSizes, setAllSizes] = useState([]);
  useEffect(() => {
    api.get(`/products/${item.product_id}`).then(({data}) => setAllSizes(data.product?.sizes || []));
  }, [item.product_id]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { item_index: itemIndex, reason };
      if (tab === "size") payload.new_size = newSize;
      else { payload.new_product_id = pickedProduct.product_id; payload.new_size = newSize; }
      const { data } = await api.post(`/orders/${orderId}/exchange`, payload);
      const diffMsg = data.diff > 0 ? ` (charged $${data.diff.toFixed(2)})` :
                      data.diff < 0 ? ` (refunded ${fmtMoney(-data.diff)})` : "";
      onDone(`Exchange done${diffMsg}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Exchange failed");
    } finally { setBusy(false); }
  };

  const canSubmit = (tab === "size" && newSize) || (tab === "product" && pickedProduct);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#261534] w-full max-w-lg rounded-3xl p-5 max-h-[85vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-black uppercase text-xl">Exchange item</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>

        <div className="flex gap-3 items-center mb-4 p-3 bg-[#3D2845] rounded-xl">
          <img src={item.image} className="w-12 h-12 object-cover rounded-lg"/>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm line-clamp-1">{item.product_name}</div>
            <div className="text-xs text-[#A1A1AA]">Size: {item.size || "—"} · {fmtMoney(item.price)}</div>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={()=>{setTab("size"); setPickedProduct(null);}} data-testid="exchange-tab-size"
            className={`chip flex-1 justify-center ${tab==="size"?"active":""}`}>Different size</button>
          <button onClick={()=>setTab("product")} data-testid="exchange-tab-product"
            className={`chip flex-1 justify-center ${tab==="product"?"active":""}`}>Different product (same store)</button>
        </div>

        {tab === "size" && (
          <div>
            <div className="text-xs uppercase font-bold mb-2">Pick a new size</div>
            <div className="flex gap-2 flex-wrap">
              {allSizes.map(s => (
                <button key={s} onClick={()=>setNewSize(s)} disabled={s===item.size}
                  data-testid={`exchange-size-${s}`}
                  className={`px-4 py-2 rounded-xl border-2 text-sm font-bold uppercase transition ${
                    newSize===s ? "border-[#D4FF00] bg-[#D4FF00]/10 text-[#D4FF00]" :
                    s===item.size ? "opacity-30 line-through border-[#3D2845]" :
                    "border-[#3D2845] hover:border-[#D4FF00]"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "product" && (
          <div>
            <div className="text-xs uppercase font-bold mb-2">Pick a new product (price diff applied to wallet)</div>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
              {products.filter(p => p.product_id !== item.product_id).map(p => {
                const active = pickedProduct?.product_id === p.product_id;
                return (
                  <button key={p.product_id} onClick={()=>{setPickedProduct(p); setNewSize(p.sizes?.[0] || "");}}
                    data-testid={`exchange-pick-${p.product_id}`}
                    className={`text-left p-2 rounded-xl border-2 transition ${active?"border-[#D4FF00]":"border-[#3D2845] hover:border-[#D4FF00]/50"}`}>
                    <img src={p.images?.[0]} className="w-full aspect-square object-cover rounded-lg mb-2"/>
                    <div className="text-xs font-bold line-clamp-1">{p.name}</div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-[#D4FF00] font-display font-bold text-sm">{fmtMoney(p.price)}</div>
                      <div className="text-[10px] text-[#A1A1AA]">{(p.price - item.price).toFixed(2) >= 0 ? "+" : ""}{fmtMoney(p.price - item.price)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {pickedProduct && (pickedProduct.sizes || []).length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase font-bold mb-2">Size for {pickedProduct.name}</div>
                <div className="flex gap-2 flex-wrap">
                  {pickedProduct.sizes.map(s => (
                    <button key={s} onClick={()=>setNewSize(s)}
                      className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold uppercase ${newSize===s?"border-[#D4FF00] bg-[#D4FF00]/10 text-[#D4FF00]":"border-[#3D2845]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <textarea placeholder="Reason (optional)" rows={2}
          value={reason} onChange={e=>setReason(e.target.value)}
          className="input-dark mt-4" data-testid="exchange-reason"/>

        {err && <div className="text-sm text-[#FF007F] mt-2">{err}</div>}

        <button onClick={submit} disabled={!canSubmit || busy} data-testid="exchange-submit-btn"
          className="btn-primary w-full mt-4">
          {busy ? "Processing…" : "Confirm exchange"}
        </button>
      </div>
    </div>
  );
}
